/**
 * La vista previa de ClinicPilot Drive (administración, 14/10/2026): «al
 * pinchar en un archivo debe abrirse una vista previa dentro de la propia
 * plataforma (imágenes, PDFs, documentos, etc.) sin que se descarguen al
 * ordenador».
 *
 * Un diálogo casi a pantalla completa (entera en el móvil) con el nombre, el
 * tamaño y la fecha, «Descargar» como OPCIÓN (no se descarga nada al abrir)
 * y «Cerrar». Las flechas (en pantalla y del teclado) pasan al archivo
 * anterior y al siguiente de la carpeta.
 *
 * Qué se enseña lo decide el SERVIDOR mirando el archivo por dentro
 * (GET /api/drive/docs/{id}/vista-previa, mismos permisos que la descarga):
 *  · PDF: en un iframe, en línea. En el móvil el iframe no sirve (Safari
 *    enseña solo la primera página), así que, como en VisorPdfPublico, se
 *    abre entero en el visor del teléfono con el enlace firmado del Drive;
 *    en la tablet, el iframe y ese botón.
 *  · Imagen (también el HEIC, que llega convertido a JPEG, y el SVG): en un
 *    <img> —nunca un SVG en iframe u <object>: dentro de un <img> no corre
 *    ningún script—, con «Tamaño real» / «Ajustar a la pantalla».
 *  · Word: el HTML que ya viene saneado del servidor, en un iframe SANDBOX
 *    SIN allow-scripts (ni allow-same-origin) y con su propia CSP.
 *  · Excel: una tabla de solo lectura, hoja a hoja, diciendo qué no se
 *    enseña por el tope.
 *  · Texto: texto plano (React lo escapa).
 *  · Lo demás (también los certificados .p12): «Vista previa no disponible»
 *    y «Descargar».
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import {
  ChevronLeft, ChevronRight, Download, X, Loader2, FileQuestion, ExternalLink,
  Maximize2, Minimize2, RotateCcw, AlertTriangle, ImageOff,
} from 'lucide-react';
import { Dialog, DialogPortal, DialogOverlay, DialogTitle } from '../ui/dialog';
import { api, formatDate } from '../../lib/api';
import { esMovil } from '../../lib/utils';
import { FichaTipo, tamano } from './fichaDrive';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/** Los bytes para pintar en línea (PDF, imagen, SVG, HEIC en JPEG). */
export const urlContenido = (id) => `${BACKEND_URL}/api/drive/docs/${id}/vista-previa/contenido`;

// El Word, ya saneado en el servidor, va además en un documento aislado con
// su propia CSP: ni scripts, ni nada de fuera; solo las imágenes que lleva
// dentro (data:) y su estilo. Los enlaces se abren en otra pestaña.
const CSP_DOCUMENTO = "default-src 'none'; img-src data:; style-src 'unsafe-inline'";
const ESTILO_DOCUMENTO = [
  'html{background:#fff}',
  'body{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;',
  'color:#13231E;font-size:15px;line-height:1.55;max-width:820px;margin:0 auto;padding:28px 32px;',
  'overflow-wrap:break-word}',
  'img{max-width:100%;height:auto}',
  'table{border-collapse:collapse;margin:12px 0}',
  'td,th{border:1px solid #d1d5db;padding:4px 8px;vertical-align:top}',
  'td p,th p{margin:0}',
  'h1,h2,h3{line-height:1.25}a{color:#2E6E5E}a:not([href]){color:inherit}',
  '@media (max-width:640px){body{padding:16px}}',
].join('');

export function documentoAislado(html) {
  return '<!doctype html><html lang="es"><head><meta charset="utf-8">'
    + `<meta http-equiv="Content-Security-Policy" content="${CSP_DOCUMENTO}">`
    + '<meta name="referrer" content="no-referrer">'
    + '<meta name="viewport" content="width=device-width,initial-scale=1">'
    + `<base target="_blank"><style>${ESTILO_DOCUMENTO}</style></head>`
    + `<body>${html}</body></html>`;
}

// Sin allow-scripts NI allow-same-origin: el documento no ejecuta nada y no
// es de la app. Los enlaces (ya solo http, https o mailto) se abren fuera;
// las anclas internas (las notas al pie) llegan del servidor sin `href`.
export const SANDBOX_DOCUMENTO = 'allow-popups allow-popups-to-escape-sandbox';

/** Letra de columna de una hoja de cálculo: 0 → A, 25 → Z, 26 → AA. */
export function letraColumna(i) {
  let n = i + 1;
  let s = '';
  while (n > 0) {
    const r = (n - 1) % 26;
    s = String.fromCharCode(65 + r) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
}

const miles = (n) => Number(n || 0).toLocaleString('es-ES');

function useConsulta(consulta) {
  const lee = () => typeof window !== 'undefined' && !!window.matchMedia?.(consulta)?.matches;
  const [coincide, setCoincide] = useState(lee);
  useEffect(() => {
    const mq = window.matchMedia?.(consulta);
    if (!mq) return undefined;
    const f = () => setCoincide(mq.matches);
    if (mq.addEventListener) mq.addEventListener('change', f); else mq.addListener?.(f);
    return () => { if (mq.removeEventListener) mq.removeEventListener('change', f); else mq.removeListener?.(f); };
  }, [consulta]);
  return coincide;
}

const botonIcono = 'inline-flex items-center justify-center h-9 min-w-9 px-2 rounded-md text-[#13231E] hover:bg-[rgba(46,110,94,0.08)] disabled:opacity-35 disabled:hover:bg-transparent focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2E6E5E]';
const botonTexto = 'inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-md border border-border bg-background text-sm font-medium text-[#13231E] hover:bg-[rgba(46,110,94,0.06)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2E6E5E]';

/** «Descargar»: en el ordenador, la descarga de siempre; en el móvil, el
 *  enlace firmado que abre fuera de la app (iOS no descarga dentro del icono
 *  de la pantalla de inicio y ahí no viaja la cookie). */
function Descargar({ doc, urlDescarga, urlAbrir, testId, conTexto = true }) {
  const movil = esMovil();
  const props = movil
    ? { href: urlAbrir(doc), target: '_blank', rel: 'noopener noreferrer' }
    : { href: urlDescarga(doc), download: doc.name };
  return (
    <a {...props} className={botonTexto} title="Descargar" aria-label={`Descargar ${doc.name}`} data-testid={testId}>
      <Download className="w-4 h-4" aria-hidden="true" />
      {conTexto && <span className="hidden sm:inline">Descargar</span>}
    </a>
  );
}

function Avisos({ lista, testId = 'drive-visor-aviso' }) {
  if (!lista?.length) return null;
  return (
    <div className="px-4 py-2 text-xs text-amber-900 bg-amber-50 border-b border-amber-200 space-y-0.5" role="note" data-testid={testId}>
      {lista.map((a) => <div key={a}>▸ {a}</div>)}
    </div>
  );
}

function VistaPdf({ src, abrir, nombre }) {
  const estrecha = useConsulta('(max-width: 767px)');
  const tactil = useConsulta('(pointer: coarse)');
  return (
    <div className="h-full flex flex-col">
      {(estrecha || tactil) && (
        <div className="p-3 border-b border-border bg-background">
          <a href={abrir} target="_blank" rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full h-11 rounded-md bg-[#2E6E5E] px-4 text-sm font-medium text-white hover:bg-[#1E4A3F]"
            data-testid="drive-visor-pdf-abrir">
            <ExternalLink className="w-4 h-4" aria-hidden="true" /> Abrir el PDF completo
          </a>
        </div>
      )}
      {estrecha ? (
        <p className="m-auto max-w-xs px-6 text-center text-sm text-muted-foreground" data-testid="drive-visor-pdf-movil">
          En el móvil el PDF se ve entero en el visor del teléfono: pulsa «Abrir el PDF completo».
        </p>
      ) : (
        <iframe src={src} title={`Vista previa de ${nombre}`} className="flex-1 w-full border-0 bg-white"
          data-testid="drive-visor-pdf" />
      )}
    </div>
  );
}

function VistaImagen({ src, nombre, formato }) {
  const [real, setReal] = useState(false);
  const [carga, setCarga] = useState('cargando'); // cargando | lista | error
  const [intento, setIntento] = useState(0);
  const conIntento = intento ? `${src}${src.includes('?') ? '&' : '?'}r=${intento}` : src;
  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center gap-2 px-3 py-1.5 border-b border-border bg-background text-xs">
        <span className="mr-auto text-muted-foreground truncate">
          {formato === 'heic' ? 'Foto HEIC convertida a JPEG para verla aquí; el archivo no cambia.' : ''}
        </span>
        <button type="button" className={botonTexto} onClick={() => setReal((v) => !v)}
          disabled={carga !== 'lista'} aria-pressed={real} data-testid="drive-visor-zoom">
          {real
            ? <><Minimize2 className="w-4 h-4" aria-hidden="true" /> Ajustar a la pantalla</>
            : <><Maximize2 className="w-4 h-4" aria-hidden="true" /> Tamaño real</>}
        </button>
      </div>
      <div className="relative flex-1 min-h-0 flex overflow-auto p-2 bg-[#e5e7eb]" data-testid="drive-visor-lienzo">
        {carga === 'error' ? (
          <div role="alert" className="m-auto max-w-xs text-center text-sm text-muted-foreground flex flex-col items-center gap-3"
            data-testid="drive-visor-imagen-error">
            <ImageOff className="w-10 h-10" aria-hidden="true" />
            <div>
              {formato === 'heic'
                ? 'No se ha podido convertir la foto para verla. Descárgala para abrirla.'
                : 'No se ha podido cargar la imagen.'}
            </div>
            <button type="button" className={botonTexto} onClick={() => { setCarga('cargando'); setIntento((n) => n + 1); }}>
              <RotateCcw className="w-4 h-4" aria-hidden="true" /> Reintentar
            </button>
          </div>
        ) : (
          // Un SVG va SIEMPRE aquí, en un <img>: así no ejecuta nada.
          <img key={conIntento} src={conIntento} alt={nombre} draggable={false}
            onLoad={() => setCarga('lista')} onError={() => setCarga('error')}
            onDoubleClick={() => { if (carga === 'lista') setReal((v) => !v); }}
            className={`m-auto bg-white shadow-sm ${real ? 'max-w-none max-h-none cursor-zoom-out' : 'max-w-full max-h-full object-contain cursor-zoom-in'}`}
            data-testid="drive-visor-imagen" data-tamano={real ? 'real' : 'ajustada'} />
        )}
        {carga === 'cargando' && (
          <div className="absolute inset-0 flex items-center justify-center gap-2 text-sm text-muted-foreground pointer-events-none">
            <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" /> Cargando…
          </div>
        )}
      </div>
    </div>
  );
}

function VistaHtml({ html, avisos, nombre }) {
  return (
    <div className="h-full flex flex-col">
      <Avisos lista={avisos} />
      {html ? (
        <iframe sandbox={SANDBOX_DOCUMENTO} srcDoc={documentoAislado(html)} referrerPolicy="no-referrer"
          title={`Vista previa de ${nombre}`} className="flex-1 w-full border-0 bg-white"
          data-testid="drive-visor-html" />
      ) : (
        <p className="m-auto text-sm text-muted-foreground">El documento no tiene texto que mostrar.</p>
      )}
    </div>
  );
}

function VistaTabla({ datos }) {
  const hojas = datos.hojas || [];
  const [activa, setActiva] = useState(0);
  const hoja = hojas[Math.min(activa, Math.max(0, hojas.length - 1))];
  if (!hoja) {
    return <p className="m-auto p-6 text-sm text-muted-foreground">La hoja de cálculo está vacía.</p>;
  }
  const filas = hoja.filas || [];
  const ancho = filas.reduce((m, f) => Math.max(m, f.length), 0);
  const avisos = [];
  if (hoja.mas_filas || hoja.mas_columnas) {
    const partes = [];
    if (hoja.mas_filas) {
      const de = hoja.filas_totales && hoja.filas_totales > filas.length ? ` de ${miles(hoja.filas_totales)}` : '';
      partes.push(`las primeras ${miles(filas.length)} filas${de}`);
    }
    if (hoja.mas_columnas) partes.push(`las primeras ${miles(datos.max_columnas)} columnas`);
    avisos.push(`Se muestran ${partes.join(' y ')}. Descarga el archivo para verlo entero.`);
  }
  if (datos.hojas_totales > hojas.length) {
    avisos.push(`Se muestran las primeras ${miles(hojas.length)} hojas de ${miles(datos.hojas_totales)}.`);
  }
  return (
    <div className="h-full flex flex-col">
      {hojas.length > 1 && (
        <div role="tablist" aria-label="Hojas" className="flex gap-1 px-3 pt-2 border-b border-border bg-background overflow-x-auto">
          {hojas.map((h, i) => (
            <button key={`${h.nombre}-${i}`} type="button" role="tab" aria-selected={i === activa}
              onClick={() => setActiva(i)}
              className={`shrink-0 px-3 py-1.5 text-xs rounded-t-md border border-b-0 ${i === activa ? 'bg-white font-semibold text-[#13231E] border-border' : 'bg-muted text-muted-foreground border-transparent hover:text-[#13231E]'}`}
              data-testid="drive-visor-hoja">
              {h.nombre}
            </button>
          ))}
        </div>
      )}
      <Avisos lista={avisos} />
      {hoja.nota ? (
        <p className="m-auto p-6 text-sm text-muted-foreground">{hoja.nota}</p>
      ) : filas.length === 0 ? (
        <p className="m-auto p-6 text-sm text-muted-foreground" data-testid="drive-visor-hoja-vacia">Esta hoja está vacía.</p>
      ) : (
        // Las flechas del teclado aquí dentro mueven la tabla, no cambian de archivo.
        <div className="flex-1 min-h-0 overflow-auto bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#2E6E5E]"
          tabIndex={0} data-flechas="propias" data-testid="drive-visor-tabla">
          <table className="text-xs border-collapse">
            <thead>
              <tr>
                <th className="sticky top-0 left-0 z-20 bg-muted border border-border min-w-[2.5rem]" aria-label="Fila" />
                {Array.from({ length: ancho }, (_, j) => (
                  <th key={j} className="sticky top-0 z-10 bg-muted border border-border px-2 py-1 font-medium text-muted-foreground">
                    {letraColumna(j)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filas.map((fila, i) => (
                <tr key={i}>
                  <th className="sticky left-0 z-10 bg-muted border border-border px-2 py-1 font-medium text-muted-foreground text-right tabular-nums">
                    {i + 1}
                  </th>
                  {Array.from({ length: ancho }, (_, j) => {
                    // Como en la hoja: cada celda en una línea; lo muy largo,
                    // cortado con «…» y entero al pasar el ratón.
                    const valor = fila[j] || '';
                    return (
                      <td key={j} className="border border-border px-2 py-1 align-top text-[#13231E]">
                        <div className="max-w-[22rem] truncate" title={valor.length > 30 ? valor : undefined}>{valor}</div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function VistaTexto({ texto, recortado, topeBytes }) {
  const mb = Math.max(1, Math.round((topeBytes || 0) / (1024 * 1024)));
  return (
    <div className="h-full flex flex-col">
      {recortado && (
        <Avisos lista={[`El archivo es largo: se muestra el primer ${mb} MB. Descárgalo para verlo entero.`]} />
      )}
      <pre className="flex-1 min-h-0 overflow-auto m-0 p-4 bg-white font-mono text-[13px] leading-relaxed whitespace-pre-wrap break-words text-[#13231E]"
        data-testid="drive-visor-texto">
        {texto}
      </pre>
    </div>
  );
}

const SIN_ARCHIVOS = [];

export default function VisorDrive({ docs: lista = SIN_ARCHIVOS, docId, onCambiar, onCerrar, urlDescarga, urlAbrir }) {
  const indice = lista.findIndex((d) => d.id === docId);
  const doc = indice >= 0 ? lista[indice] : null;
  const total = lista.length;
  // `para`: de qué archivo es lo cargado. Al pasar al siguiente, lo del
  // anterior no se pinta ni un instante con el archivo nuevo.
  const [estado, setEstado] = useState({ para: null, datos: null, error: null });
  const [intento, setIntento] = useState(0);
  const cerrarRef = useRef(null);

  // Lo que hay que enseñar lo dice el servidor, archivo a archivo. Al pasar
  // a otro (o cerrar), la petición del anterior se CANCELA (revisión del
  // 14/10/2026): pasar deprisa por cinco Word no deja cinco conversiones
  // pedidas, ni su respuesta pinta encima del archivo nuevo.
  useEffect(() => {
    if (!docId) return undefined;
    const control = new AbortController();
    setEstado({ para: null, datos: null, error: null });
    api.get(`/api/drive/docs/${docId}/vista-previa`, { signal: control.signal })
      .then((r) => { if (!control.signal.aborted) setEstado({ para: docId, datos: r.data || null, error: null }); })
      .catch((e) => {
        if (!control.signal.aborted) {
          setEstado({
            para: docId,
            datos: null,
            error: e?.response?.data?.detail || 'Comprueba la conexión y vuelve a intentarlo.',
          });
        }
      });
    return () => control.abort();
  }, [docId, intento]);

  // El archivo ya no está en la lista (se ha borrado o movido): se cierra.
  useEffect(() => {
    if (docId && !doc) onCerrar();
  }, [docId, doc, onCerrar]);

  const anterior = useCallback(() => {
    if (indice > 0) onCambiar(lista[indice - 1].id);
  }, [indice, lista, onCambiar]);
  const siguiente = useCallback(() => {
    if (indice >= 0 && indice < total - 1) onCambiar(lista[indice + 1].id);
  }, [indice, total, lista, onCambiar]);

  // Las flechas del teclado. Esc lo lleva el diálogo.
  useEffect(() => {
    if (!doc) return undefined;
    const alTeclear = (e) => {
      if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
      const t = e.target;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName || ''))) return;
      if (t?.closest?.('[data-flechas="propias"]')) return;
      if (e.key === 'ArrowLeft') { e.preventDefault(); anterior(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); siguiente(); }
    };
    document.addEventListener('keydown', alTeclear);
    return () => document.removeEventListener('keydown', alTeclear);
  }, [doc, anterior, siguiente]);

  if (!doc) return null;

  const cargando = estado.para !== docId;
  const { datos, error } = estado;
  const descargarCuerpo = (
    <Descargar doc={doc} urlDescarga={urlDescarga} urlAbrir={urlAbrir} testId="drive-visor-descargar-cuerpo" />
  );

  let cuerpo;
  if (cargando) {
    cuerpo = (
      <div className="h-full flex items-center justify-center gap-2 text-sm text-muted-foreground" data-testid="drive-visor-cargando">
        <Loader2 className="w-5 h-5 animate-spin text-[#2E6E5E]" aria-hidden="true" /> Preparando la vista previa…
      </div>
    );
  } else if (error) {
    cuerpo = (
      <div role="alert" className="h-full flex flex-col items-center justify-center gap-3 px-6 text-center" data-testid="drive-visor-error">
        <AlertTriangle className="w-10 h-10 text-amber-600" aria-hidden="true" />
        <div className="text-sm font-medium text-[#13231E]">No se ha podido preparar la vista previa.</div>
        <div className="text-xs text-muted-foreground max-w-sm">{error}</div>
        <div className="flex items-center gap-2">
          <button type="button" className={botonTexto} onClick={() => setIntento((n) => n + 1)} data-testid="drive-visor-reintentar">
            <RotateCcw className="w-4 h-4" aria-hidden="true" /> Reintentar
          </button>
          {descargarCuerpo}
        </div>
      </div>
    );
  } else if (datos?.tipo === 'pdf') {
    cuerpo = <VistaPdf src={urlContenido(doc.id)} abrir={urlAbrir(doc)} nombre={doc.name} />;
  } else if (datos?.tipo === 'imagen') {
    cuerpo = <VistaImagen key={doc.id} src={urlContenido(doc.id)} nombre={doc.name} formato={datos.formato} />;
  } else if (datos?.tipo === 'html') {
    cuerpo = <VistaHtml html={datos.html} avisos={datos.avisos} nombre={doc.name} />;
  } else if (datos?.tipo === 'tabla') {
    cuerpo = <VistaTabla key={doc.id} datos={datos} />;
  } else if (datos?.tipo === 'texto') {
    cuerpo = <VistaTexto texto={datos.texto} recortado={datos.recortado} topeBytes={datos.tope_bytes} />;
  } else {
    const motivo = datos?.motivo && datos.motivo !== 'Vista previa no disponible para este formato.' ? datos.motivo : '';
    cuerpo = (
      <div className="h-full flex flex-col items-center justify-center gap-3 px-6 text-center" data-testid="drive-visor-no-disponible">
        <FileQuestion className="w-12 h-12 text-muted-foreground" aria-hidden="true" />
        <div className="text-sm font-medium text-[#13231E]">Vista previa no disponible para este formato</div>
        {motivo && <div className="text-xs text-muted-foreground max-w-sm">{motivo}</div>}
        {descargarCuerpo}
      </div>
    );
  }

  const fecha = doc.created_at ? formatDate(doc.created_at.slice(0, 10)) : '';
  return (
    <Dialog open onOpenChange={(abierto) => { if (!abierto) onCerrar(); }}>
      <DialogPortal>
        <DialogOverlay className="z-[70] bg-black/70" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          // El foco, a «Cerrar»: lo primero que se busca con el teclado.
          onOpenAutoFocus={(e) => { e.preventDefault(); cerrarRef.current?.focus(); }}
          className="fixed inset-0 sm:inset-[2vh_2vw] z-[70] flex flex-col bg-background sm:rounded-xl shadow-2xl overflow-hidden outline-none"
          data-testid="drive-visor"
        >
          <div className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 pb-2.5 pt-[max(0.625rem,env(safe-area-inset-top))] border-b border-border">
            <span className="hidden sm:inline-flex"><FichaTipo doc={doc} size={34} /></span>
            <div className="flex-1 min-w-0">
              <DialogTitle className="text-sm sm:text-base font-semibold text-[#13231E] truncate" data-testid="drive-visor-nombre">
                {doc.name}
              </DialogTitle>
              <div className="text-xs text-muted-foreground truncate" data-testid="drive-visor-datos">
                {[tamano(doc.size), fecha].filter(Boolean).join(' · ')}
              </div>
            </div>
            {total > 1 && (
              <div className="flex items-center">
                <button type="button" className={botonIcono} onClick={anterior} disabled={indice <= 0}
                  aria-label="Archivo anterior" title="Anterior (←)" data-testid="drive-visor-anterior">
                  <ChevronLeft className="w-5 h-5" aria-hidden="true" />
                </button>
                <span className="hidden sm:inline min-w-[3.5rem] text-center text-xs tabular-nums text-muted-foreground"
                  data-testid="drive-visor-cuenta">
                  {indice + 1} / {total}
                </span>
                <button type="button" className={botonIcono} onClick={siguiente} disabled={indice >= total - 1}
                  aria-label="Archivo siguiente" title="Siguiente (→)" data-testid="drive-visor-siguiente">
                  <ChevronRight className="w-5 h-5" aria-hidden="true" />
                </button>
              </div>
            )}
            <Descargar doc={doc} urlDescarga={urlDescarga} urlAbrir={urlAbrir} testId="drive-visor-descargar" />
            <button ref={cerrarRef} type="button" className={botonTexto} onClick={onCerrar}
              aria-label="Cerrar" title="Cerrar (Esc)" data-testid="drive-visor-cerrar">
              <X className="w-4 h-4" aria-hidden="true" />
              <span className="hidden sm:inline">Cerrar</span>
            </button>
          </div>
          <div className="relative flex-1 min-h-0 bg-[#f3f4f6]" data-testid="drive-visor-cuerpo" data-tipo={datos?.tipo || ''}>
            {cuerpo}
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}
